import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as DownArrow } from '../../assets/icons/down-arrow.svg';
import { brandIcons } from '../../config/branding';

type ValueMode = 'amount' | 'percentage';

interface InputWithDropDownInterface {
  label: string;
  value: string | undefined;
  mode: ValueMode;
  onChangeValue: (value: string) => void;
  onChangeMode: (mode: ValueMode) => void;
}

/**
 * Custom input field with dropdown
 * @param {any} properties Component properties
 * @returns {any} input with dropdown
 */
export default function InputWithDropDown(properties: InputWithDropDownInterface) {
  const {
    label,
    value: propertyValue = '',
    mode: propertyMode = 'amount',
    onChangeValue,
    onChangeMode,
  } = properties;
  const [mode, setMode] = useState<ValueMode>(propertyMode);
  const [value, setValue] = useState<string>(propertyValue);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [style, setStyle] = useState<React.CSSProperties>({});
  const wrapperReference = useRef<HTMLDivElement>(null);
  const dropdownReference = useRef<HTMLDivElement>(null);

  const CurrencyIcon = brandIcons.currencyIcon;

  /**
   * Toggle dropdown open/close
   */
  const toggleDropdown = () => {
    if (!isOpen && wrapperReference.current) {
      const rect = wrapperReference.current.getBoundingClientRect();
      const dropdownWidth = -150; // adjust to your CSS width
      const margin = 1;

      const top = rect.bottom + window.scrollY;
      let left = rect.left + rect.width / 2 - dropdownWidth / 2;

      if (left < margin) left = margin;
      if (left + dropdownWidth > window.innerWidth - margin) {
        left = window.innerWidth - dropdownWidth - margin;
      }

      setStyle({
        position: 'absolute',
        top,
        left,
        width: dropdownWidth,
        zIndex: 9999,
      });
    }
    setIsOpen((previous) => !previous);
  };

  /**
   * Handling input values (limit to 2 decimals)
   * @param {any} event input change event
   */
  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;

    // Allow only numbers and one dot
    inputValue = inputValue.replace(/[^0-9.]/g, '');

    // Split into integer and decimal
    const parts = inputValue.split('.');
    if (parts.length > 2) {
      inputValue = `${parts[0]}.${parts[1]}`; // use template literal
    }

    // Limit decimals to 2
    if (parts[1] && parts[1].length > 2) {
      inputValue = `${parts[0]}.${parts[1].slice(0, 2)}`; // use template literal
    }

    setValue(inputValue);
    onChangeValue?.(inputValue);
  };

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    /**
     * Close dropdown when clicking outside
     * @param {any} event - The event
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownReference.current &&
        !dropdownReference.current.contains(event.target as Node) &&
        !wrapperReference.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Handle selecting a mode
   * @param {any} newMode - The newMode
   */
  const selectMode = (newMode: ValueMode) => {
    setMode(newMode);
    onChangeMode?.(newMode);
    setIsOpen(false);
  };

  useEffect(() => {
    setValue(propertyValue);
  }, [propertyValue]);

  useEffect(() => {
    setMode(propertyMode);
  }, [propertyMode]);

  return (
    <div className="input-with-dropdown" ref={wrapperReference}>
      <div className="input-label">{label}</div>

      <div className="input-field">
        <input
          type="number"
          placeholder="Enter Value"
          value={value}
          onChange={(event) => handleInput(event)}
          aria-label="Value input"
        />

        <div className="dropdown-wrapper">
          <button
            type="button"
            className="toggle-button"
            onClick={toggleDropdown}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls="dropdown-menu">
            {mode === 'amount' ? <CurrencyIcon /> : '%'}
            <DownArrow className={`arrow-icon ${isOpen ? 'open' : ''}`} />
          </button>
        </div>
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            id="dropdown-menu"
            className="dropdown-menu-content"
            style={style}
            ref={dropdownReference}
            role="listbox">
            <div
              className={`dropdown-item ${mode === 'amount' ? 'active' : ''}`}
              onClick={() => selectMode('amount')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  selectMode('amount');
                }
              }}
              role="option"
              aria-selected={mode === 'amount'}
              tabIndex={0}>
              <CurrencyIcon />
            </div>

            <div
              className={`dropdown-item ${mode === 'percentage' ? 'active' : ''}`}
              onClick={() => selectMode('percentage')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  selectMode('percentage');
                }
              }}
              role="option"
              aria-selected={mode === 'percentage'}
              tabIndex={0}>
              %
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}